import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';

/**
 * Affiliate program API (foundation ticket). Shapes verified with real curls
 * against backend main (PR #241): integer cents everywhere, lastPayout.paidAt
 * (NOT .date), currentMonthEarningsCents = pending + eligible for the current
 * UTC month.
 */

export interface AffiliateProfile {
  paypalEmail: string;
  enrolledAt: string;
  status: 'active' | 'paused' | 'banned';
  termsVersion: string;
}

export interface AffiliateLastPayout {
  amountCents: number;
  paidAt: string;
}

export interface AffiliateStats {
  /** Pending + eligible earned in the current UTC month ("Current Month Earnings"). */
  currentMonthEarningsCents: number;
  allTimeEarningsCents: number;
  totalConversions: number;
  lastPayout: AffiliateLastPayout | null;
}

export interface GetMyAffiliateResponse {
  enrolled: boolean;
  affiliate?: AffiliateProfile;
  stats?: AffiliateStats;
}

export type AffiliateEarningStatus = 'pending' | 'eligible' | 'paid' | 'voided';

export interface AffiliateEarning {
  id: number;
  subscriptionDate: string;
  amountCents: number;
  status: AffiliateEarningStatus;
  referredUsername: string;
  planInterval: 'monthly' | 'annual' | null;
  payoutBatchId: string | null;
}

export interface GetMyEarningsResponse {
  earnings: AffiliateEarning[];
  total: number;
}

export interface GetMyEarningsParams {
  page: number;
  pageSize?: number;
  sortBy?: 'date' | 'amount';
  sortDir?: 'asc' | 'desc';
}

export interface AffiliateDashboardMonth {
  /** "YYYY-MM", UTC calendar month. */
  month: string;
  clicks: number;
  conversions: number;
  earningsCents: number;
}

export interface AffiliateDashboardTotals {
  allTimeClicks: number;
  /** Same number as stats.totalConversions on GET /affiliates/me. */
  allTimeConversions: number;
  /** FRACTION 0–1 (multiply by 100 for display); 0 when clicks are 0. */
  allTimeConversionRate: number;
  currentMonthEarningsCents: number;
  allTimeEarningsCents: number;
  lastPayout: AffiliateLastPayout | null;
}

export interface AffiliateDashboardResponse {
  /** Exactly 12 zero-filled ascending months, current UTC month last. */
  months: AffiliateDashboardMonth[];
  totals: AffiliateDashboardTotals;
}

export interface ResolveAffiliateResponse {
  valid: boolean;
  /** Canonical-cased username when valid. */
  username?: string;
}

export interface EnrollAffiliateRequest {
  paypalEmail: string;
  termsVersion: string;
}

// ── Admin (contract items 9–12) ─────────────────────────────────────────────

export interface AdminAffiliateEarning {
  id: number;
  affiliateUsername: string;
  affiliateUserId: number;
  paypalEmail: string | null;
  referredUsername: string;
  referredUserId: number;
  subscriptionDate: string;
  stripeChargeId: string | null;
  status: AffiliateEarningStatus;
  amountCents: number;
  createdAt: string;
  eligibleAt: string | null;
  paidAt: string | null;
  paidBy: number | null;
  voidedAt: string | null;
  voidReason: string | null;
  payoutBatchId: string | null;
}

export interface AdminEarningsSummary {
  pending: { count: number; totalCents: number };
  eligible: { count: number; totalCents: number };
  paid: { count: number; totalCents: number };
  voided: { count: number; totalCents: number };
}

export interface AdminEarningsResponse {
  /** Current UTC calendar month, per status. */
  summary: AdminEarningsSummary;
  earnings: AdminAffiliateEarning[];
  total: number;
}

export interface AdminEarningsParams {
  statuses?: AffiliateEarningStatus[];
  from?: string;
  to?: string;
  username?: string;
  page?: number;
  pageSize?: number;
  sortBy?: 'createdAt' | 'amount' | 'status' | 'subscriptionDate';
  sortDir?: 'asc' | 'desc';
}

export const buildAdminEarningsQueryString = (params: AdminEarningsParams): string => {
  const qs = new URLSearchParams();
  if (params.statuses?.length) qs.append('status', params.statuses.join(','));
  if (params.from) qs.append('from', params.from);
  if (params.to) qs.append('to', params.to);
  if (params.username?.trim()) qs.append('username', params.username.trim());
  if (params.page) qs.append('page', String(params.page));
  if (params.pageSize) qs.append('pageSize', String(params.pageSize));
  if (params.sortBy) qs.append('sortBy', params.sortBy);
  if (params.sortDir) qs.append('sortDir', params.sortDir);
  return qs.toString();
};

export const affiliatesApi = createApi({
  reducerPath: 'affiliatesApi',
  baseQuery: baseQuery,
  tagTypes: ['Affiliate', 'AffiliateEarnings', 'AdminAffiliateEarnings'],
  endpoints: (builder) => ({
    // Public: resolve an /elite/:slug code to an active affiliate.
    resolveAffiliateSlug: builder.query<ResolveAffiliateResponse, string>({
      query: (slug) => ({
        url: `/affiliates/resolve/${encodeURIComponent(slug)}`,
        method: 'GET',
        requiresAuth: false,
      }),
    }),

    // Public: async click logging (backend responds 202 and dedupes per anon id / 24h).
    logAffiliateClick: builder.mutation<
      void,
      { slug: string; landingUrl: string; anonymousId: string }
    >({
      query: (body) => ({
        url: '/affiliates/click',
        method: 'POST',
        body,
        requiresAuth: false,
      }),
    }),

    getMyAffiliate: builder.query<GetMyAffiliateResponse, void>({
      query: () => ({
        url: '/affiliates/me',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Affiliate'],
    }),

    enrollAffiliate: builder.mutation<
      { affiliate: AffiliateProfile },
      EnrollAffiliateRequest
    >({
      query: (body) => ({
        url: '/affiliates/enroll',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['Affiliate'],
    }),

    updateMyAffiliate: builder.mutation<{ affiliate: AffiliateProfile }, { paypalEmail: string }>({
      query: (body) => ({
        url: '/affiliates/me',
        method: 'PATCH',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['Affiliate'],
    }),

    // Dashboard (ticket 1216578268363052). 403 body is exactly
    // {"error":"NOT_ENROLLED"} for a logged-in non-enrolled user — the FE
    // redirect signal to Settings > Affiliates. Unauthenticated is a plain 401.
    getAffiliateDashboard: builder.query<AffiliateDashboardResponse, void>({
      query: () => ({
        url: '/affiliates/dashboard',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['Affiliate', 'AffiliateEarnings'],
    }),

    getMyEarnings: builder.query<GetMyEarningsResponse, GetMyEarningsParams>({
      query: ({ page, pageSize = 25, sortBy = 'date', sortDir = 'desc' }) => ({
        url: `/affiliates/me/earnings?page=${page}&pageSize=${pageSize}&sortBy=${sortBy}&sortDir=${sortDir}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['AffiliateEarnings'],
    }),

    // ── Admin ────────────────────────────────────────────────────────────────

    getAdminEarnings: builder.query<AdminEarningsResponse, AdminEarningsParams>({
      query: (params) => ({
        url: `/admin/affiliates/earnings?${buildAdminEarningsQueryString(params)}`,
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['AdminAffiliateEarnings'],
    }),

    markEarningsPaid: builder.mutation<
      { payoutBatchId: string; updatedCount: number },
      { earningIds: number[] }
    >({
      query: (body) => ({
        url: '/admin/affiliates/earnings/mark-paid',
        method: 'POST',
        body,
        requiresAuth: true,
      }),
      invalidatesTags: ['AdminAffiliateEarnings', 'AffiliateEarnings', 'Affiliate'],
    }),

    voidEarning: builder.mutation<unknown, { id: number; reason: string }>({
      query: ({ id, reason }) => ({
        url: `/admin/affiliates/earnings/${id}/void`,
        method: 'POST',
        body: { reason },
        requiresAuth: true,
      }),
      invalidatesTags: ['AdminAffiliateEarnings', 'AffiliateEarnings', 'Affiliate'],
    }),
  }),
});

export const {
  useResolveAffiliateSlugQuery,
  useLogAffiliateClickMutation,
  useGetMyAffiliateQuery,
  useGetAffiliateDashboardQuery,
  useEnrollAffiliateMutation,
  useUpdateMyAffiliateMutation,
  useGetMyEarningsQuery,
  useGetAdminEarningsQuery,
  useMarkEarningsPaidMutation,
  useVoidEarningMutation,
} = affiliatesApi;
